import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import { pathOr } from 'ramda'
import { RootState } from 'store'

import RolesRepository from 'services/repositories/RolesRepository'
import { IRole } from 'types/roles'
import { IQuery, IRolesState } from 'types/slices'
import { LoadingStatus, TLoadingStatus } from 'types/user'
import { handleApiErrors } from 'utils/errors'

const initialState: IRolesState = {
  status: 'initial',
  roles: [],
  permissions: [],
  role: {
    id: '',
    name: '',
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
    rolePermissions: []
  }
}

export const types = {
  getAllRoles: 'roles/getAllRoles',
  getAllPermissions: 'roles/getAllPermissions',
  getRoleById: 'roles/getRoleById'
} as const

export const getAllRoles = createAsyncThunk(
  types.getAllRoles,
  async (query: IQuery, { rejectWithValue }) => {
    try {
      const response = await RolesRepository.getAllRoles({ query })

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getAllPermissions = createAsyncThunk(
  types.getAllPermissions,
  async (_, { rejectWithValue }) => {
    try {
      const response = await RolesRepository.getAllPermissions()

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const getRoleById = createAsyncThunk(
  types.getRoleById,
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await RolesRepository.getRoleById(id)

      return response.data
    } catch (err: any) {
      return rejectWithValue(err.response.data)
    }
  }
)

export const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllRoles.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAllRoles.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.roles = action.payload
      })
      .addCase(getAllRoles.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getAllPermissions.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getAllPermissions.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.permissions = action.payload
      })
      .addCase(getAllPermissions.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
      .addCase(getRoleById.pending, state => {
        state.status = LoadingStatus.loading
      })
      .addCase(getRoleById.fulfilled, (state, action) => {
        state.status = LoadingStatus.loaded
        state.role = action.payload
      })
      .addCase(getRoleById.rejected, (state, err) => {
        state.status = LoadingStatus.failed
        handleApiErrors(err, false)
      })
  }
})

export const selectRoles = (state: RootState) => state.roles
export const selectStatus: (state: RootState) => TLoadingStatus =
  createSelector(selectRoles, rolesSlice => rolesSlice.status)

export const selectAllPartners: (state: RootState) => IRole[] =
  createSelector(selectRoles, pathOr([], ['roles']))

export const selectAllPermissions: (state: RootState) => string[] =
  createSelector(selectRoles, rolesSlice => rolesSlice.permissions)

export const selectRole: (state: RootState) => IRole = createSelector(
  selectRoles,
  rolesSlice => rolesSlice.role
)

export default rolesSlice.reducer
